/**
 * BulkActionBar — shared pieces for the row-checkbox bulk actions on the
 * Clients / Engagements / Compliance list pages.
 *
 * `postBulk` calls one of the /bulk endpoints and returns the per-id result.
 * `BulkActionBar` is the sticky bottom bar; `BulkResultToast` shows the
 * "updated n, failed m" outcome with expandable failure reasons.
 * `StaffSelect` is the keep/clear/reassign user picker used by assign modals.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserOutput } from '@1financial/shared';
import { getAccessToken, authenticatedFetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { SearchableSelect } from './SearchableSelect.js';
import { useEscape } from '../hooks/useEscape.js';

export interface BulkFailure {
  id: string;
  error: string;
}
export interface BulkResult {
  updated: number;
  failed: BulkFailure[];
}

export async function postBulk(path: string, payload: unknown): Promise<BulkResult> {
  const res = await authenticatedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error?.message ?? res.statusText);
  return body.data as BulkResult;
}

export function BulkActionBar({ count, onClear, children }: {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}) {
  useEscape(onClear);
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-2 bg-bg-panel border border-border rounded-xl px-4 py-2.5 shadow-2xl max-w-[95vw]">
      <span className="text-sm text-white font-medium mr-1">{count} selected</span>
      {children}
      <button onClick={onClear} className="text-xs text-muted hover:text-white px-2 py-1">Clear</button>
    </div>
  );
}

export function BulkActionButton({ onClick, danger, disabled, children }: {
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        danger
          ? 'text-xs btn-ghost-danger border px-2.5 py-1.5 rounded-lg disabled:opacity-50'
          : 'text-xs text-muted hover:text-white border border-border hover:border-accent px-2.5 py-1.5 rounded-lg disabled:opacity-50'
      }
    >
      {children}
    </button>
  );
}

export function BulkResultToast({ result, onClose }: { result: BulkResult; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const ok = result.failed.length === 0;

  useEffect(() => {
    if (!ok) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [ok, onClose]);

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 max-w-sm bg-bg-panel border rounded-lg px-4 py-2.5 text-sm shadow-2xl ${
        ok ? 'border-accent text-emerald-300' : 'border-amber-700 text-amber-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <span>
          Updated {result.updated}
          {result.failed.length > 0 ? `, failed ${result.failed.length}` : ''}
        </span>
        {result.failed.length > 0 && (
          <button onClick={() => setExpanded((v) => !v)} className="text-xs underline">
            {expanded ? 'Hide' : 'Details'}
          </button>
        )}
        <button onClick={onClose} className="text-xs text-muted hover:text-white ml-auto">✕</button>
      </div>
      {expanded && result.failed.length > 0 && (
        <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
          {result.failed.map((f) => (
            <p key={f.id} className="text-xs text-rose-300">
              {f.id.slice(0, 8)}…: {f.error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function useStaffUsers() {
  return useQuery({
    queryKey: ['staff-users'],
    queryFn: async () => {
      const res = await authenticatedFetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items.filter((u) => u.status !== 'DISABLED');
    },
  });
}

/**
 * User picker for bulk assign/owner modals.
 * mode 'keep': undefined = "keep current", null = "clear", string = user id.
 * mode 'required': null = "clear", string = user id (no keep option).
 */
export function StaffSelect({ value, onChange, mode = 'keep', disabled }: {
  value: string | null | undefined;
  onChange: (v: string | null | undefined) => void;
  mode?: 'keep' | 'required';
  disabled?: boolean;
}) {
  const users = useStaffUsers();
  const selectValue = value === undefined ? '' : value === null ? '__clear' : value;
  return (
    <SearchableSelect
      value={selectValue}
      disabled={disabled || users.isLoading}
      onChange={(v) => onChange(v === '' ? undefined : v === '__clear' ? null : v)}
      placeholder={mode === 'keep' ? '— keep current —' : '— select —'}
      clearable={false}
      options={[
        ...(mode === 'keep' ? [{ value: '', label: '— keep current —' }] : []),
        { value: '__clear', label: '— clear —' },
        ...(users.data ?? []).map((u) => ({ value: u.id, label: `${u.displayName} (${u.email})` })),
      ]}
    />
  );
}
